import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Navigate,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import App from "./App.tsx";
import { ConfirmationPage } from "./ConfirmationPage.tsx";
import { RootLayoutComponent } from "./RootLayoutComponent.tsx";

const appRootLayout = createRootRoute({
  component: () => <Outlet />,
});

const publicLayout = createRoute({
  getParentRoute: () => appRootLayout,
  id: "publicLayout",
  component: RootLayoutComponent,
});

const indexRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: "/",
  component: App,
});

export const confirmRoute = createRoute({
  getParentRoute: () => publicLayout,
  path: "/confirm/$confirmationCode",
  component: ConfirmationPage,
  beforeLoad: async ({ params }) => {
    if (!params.confirmationCode) {
      throw redirect({ to: "/" });
    }
  },
});

const routeChildren = [publicLayout.addChildren([indexRoute, confirmRoute])];

// The form editor is a dev-only tool. Keeping its route, its component import
// and the dynamic `import()` inside this `import.meta.env.DEV` block means a
// production build (`vite build`) statically evaluates the guard to `false`
// and dead-code-eliminates the whole block — so the route, the FormEditor
// component and the heavy @astroapps/schemas-editor dependency are never
// emitted into the production bundle.
if (import.meta.env.DEV) {
  const adminLayout = createRoute({
    getParentRoute: () => appRootLayout,
    id: "adminLayout",
    component: () => <Outlet />,
  });

  const formEditor = createRoute({
    getParentRoute: () => adminLayout,
    path: "/editor",
    component: lazyRouteComponent(() =>
      import("hwc-common/hwc/FormEditor.tsx").then((m) => ({
        default: m.FormEditor,
      })),
    ),
  });

  // Cast keeps the production route-tree types (and `to` autocomplete) intact;
  // the editor route only exists in dev.
  (routeChildren as unknown[]).push(adminLayout.addChildren([formEditor]));
}

const routeTree = appRootLayout.addChildren(routeChildren);

export const router = createRouter({
  routeTree,
  defaultNotFoundComponent: () => <Navigate to="/" />,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
